/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import platform from 'platform';
import bowser from 'bowser';
import { getAppLocale } from '@intl/adobe-locales';
import * as url from 'url';
import { setCCHomeLangCookie, supportedLanguages } from '../utils/Locale';
import { getCookie } from '../utils/cookie';
import LoggerUtils from '../utils/LoggerUtils';
import LocalStorageUtils from '../utils/LocalStorageUtils';
import SessionStorageUtils from '../utils/SessionStorageUtils';
import { murmurHashUserId } from './ExperimentationUtils';
import AnalyticsUtil from '../utils/AnalyticsUtil';
import UserTypeUtil from '@cchome/home-components/src/components/PersonalizedHome/UserTypeUtil';
import ProfileStorage from '../src/personalization/ProfileStorage';
import { DeviceDetectionUtil } from 'cchome-shared/utils';

declare global {
	interface Navigator {
		browserLanguage: any;
	}
}

let _currentBreakpoint = '';
export class Utils {
	// Singleton
	static instance;
	// this indicates whether the current session is this user's first visit to CC Home.
	firstVisit;

	constructor() {
		if (!Utils.instance) {
			window.addEventListener('analyticsOptinInited', this.sendDeferredMetric);

			window.addEventListener('resize', this.onResize);
			this.onResize();

			Utils.instance = this;
		}
		return Utils.instance;
	}

	/**
	 *
	 */
	sendDeferredMetric() {
		AnalyticsUtil.sendDeferredMetric();
	}

	/**
	 * Join the old and the new feature flag set together
	 */
	joinFeatureSet() {
		if (window.cchome.util.addFeaturesArray && window.cchome.util.addFeaturesArray.length) {
			for (const index in window.cchome.util.addFeaturesArray) {
				const urlFF = window.cchome.util.addFeaturesArray[index];
				// Check to see if the user is changing buckets
				// Buckets should be delimited with ':'.
				if (urlFF.includes(':')) {
					const res = urlFF.split(':');
					window.featureFlags.features[res[0]] = parseInt(res[1]);
				} else {
					window.featureFlags.features[urlFF] = 1;
				}
			}
		}

		if (window.cchome.util.removeFeaturesArray && window.cchome.util.removeFeaturesArray.length) {
			for (const index in window.cchome.util.removeFeaturesArray) {
				window.featureFlags.features[window.cchome.util.removeFeaturesArray[index]] = 0;
			}
		}
	}

	// returns all the features
	getAllFeatures() {
		let allFeatures: string[] = [];

		if (
			window.cchome.config &&
			window.cchome.config.features &&
			Array.isArray(window.cchome.config.features) &&
			window.cchome.config.features.length > 0
		) {
			allFeatures = allFeatures.concat(window.cchome.config.features);
		}

		if (window.featureFlags && typeof window.featureFlags.features === 'object') {
			if (
				Array.isArray(Object.keys(window.featureFlags.features)) &&
				Object.keys(window.featureFlags.features).length > 0
			) {
				Object.keys(window.featureFlags.features).forEach((key) => {
					if (window.featureFlags.features[key] > 0) {
						allFeatures = allFeatures.concat(key);
					}
				});
			}
		}

		return allFeatures;
	}

	// detect if the provided feature name is enabled
	hasFeature(feature) {
		if (
			window.featureFlags &&
			window.featureFlags.features &&
			typeof window.featureFlags.features[feature] !== 'undefined' &&
			window.featureFlags.features[feature] !== 0
		) {
			return true;
		}

		if (window.cchome.config && window.cchome.config.features) {
			return 0 <= window.cchome.config.features.indexOf(feature);
		}
		return false;
	}

	// tells whether the given feature indicates a test or control type of user
	getFeatureFlagVariant(feature: { variant_id: number }) {
		if (feature.variant_id === 0) {
			return 'control';
		}
		return 'test';
	}

	// get bucket for feature flag
	getBucket(feature: string) {
		let bucket = -1;
		if (
			window.featureFlags &&
			window.featureFlags.features &&
			typeof window.featureFlags.features[feature] !== 'undefined' &&
			window.featureFlags.features[feature] !== 0
		) {
			bucket = window.featureFlags.features[feature];
		}
		return bucket;
	}

	// AAM PLugin related helper functions

	// get the AAM plugin installed on the current browser
	getAAMPlugin() {
		let aamPlugin: any = null;

		if (navigator && navigator.plugins) {
			aamPlugin = Object.values(navigator.plugins).find(
				(plugin) => plugin && plugin.name && plugin.name.toLowerCase().indexOf('adobeaamdetect') > -1,
			);
		}

		if (!aamPlugin && platform.name === 'IE') {
			try {
				aamPlugin = new window.ActiveXObject('AdobeAAMDetect.AdobeAAMDetect');
			} catch (e) {
				return null;
			}
		}

		return aamPlugin;
	}

	// // get version of the AAM plugin installed on the current browser
	getAAMPluginVersion() {
		let aamVersion: any = null;
		let aamPlugin: any = null;
		// Win: Creative Cloud Desktop Plugin.v_2_0_0_0
		// Mac: A plugin to detect whether the Adobe Creative Cloud is installed on this machine.v_2_0_0_0
		if (navigator && navigator.plugins) {
			aamPlugin = Object.values(navigator.plugins).find(
				(plugin) => plugin && plugin.name && plugin.name.toLowerCase().indexOf('adobeaamdetect') > -1,
			);

			// Retrieve version info from description

			/* istanbul ignore else */
			if (aamPlugin && aamPlugin.description) {
				aamVersion = aamPlugin.description.match(/[0-9]_[0-9]_[0-9]_[0-9]/i);

				/* istanbul ignore else */
				if (aamVersion && aamVersion[0]) {
					aamVersion = aamVersion[0].replace(/_/g, '.');
				}
			}
		}

		// Win IE: x.0.0.0
		if (!aamPlugin && platform.name === 'IE') {
			try {
				aamPlugin = new window.ActiveXObject('AdobeAAMDetect.AdobeAAMDetect');

				/* istanbul ignore next */
				if (aamPlugin && aamPlugin.version) {
					aamVersion = aamPlugin.version.match(/[0-9].[0-9].[0-9].[0-9]/i);
					if (aamVersion && aamVersion[0]) {
						aamVersion = aamVersion[0];
					}
				}
			} catch (e) {
				return null;
			}
		}

		return aamVersion;
	}

	/* Currently, there appears to be no foolproof way to detect this, but we'll get as close as we can */
	is64bitWindows() {
		return DeviceDetectionUtil.is64bitWindows();
	}

	getOSVersion() {
		return DeviceDetectionUtil.getOSVersion();
	}

	isWin() {
		return DeviceDetectionUtil.isWin();
	}

	isMacOS() {
		return DeviceDetectionUtil.isMacOS();
	}

	is64bitMac() {
		return DeviceDetectionUtil.is64bitMac();
	}

	isFirefox() {
		return DeviceDetectionUtil.isFirefox();
	}

	isChrome() {
		return DeviceDetectionUtil.isChrome();
	}

	isSafari() {
		return DeviceDetectionUtil.isSafari();
	}

	isEdge() {
		return DeviceDetectionUtil.isEdge();
	}

	isIE() {
		return DeviceDetectionUtil.isInternetExplorer();
	}

	getBrowserType() {
		if (this.isIE()) {
			return 'ie';
		}

		if (this.isEdge()) {
			return 'edge';
		}
		if (this.isChrome()) {
			return 'chrome';
		}
		if (this.isSafari()) {
			return 'safari';
		}
		if (this.isFirefox()) {
			return 'firefox';
		}
	}

	// Checking browsers that cannot run CCHome
	isUnsupportedBrowser() {
		return (
			bowser.isUnsupportedBrowser({ chrome: '24' }, window.navigator.userAgent) ||
			bowser.isUnsupportedBrowser({ safari: '8' }, window.navigator.userAgent) ||
			bowser.isUnsupportedBrowser({ firefox: '29' }, window.navigator.userAgent) ||
			bowser.isUnsupportedBrowser({ msedge: '12' }, window.navigator.userAgent) ||
			bowser.isUnsupportedBrowser({ msie: '11' }, window.navigator.userAgent)
		);
	}

	isUnsupportedIE() {
		return bowser.isUnsupportedBrowser({ msie: '11' }, window.navigator.userAgent);
	}

	// Checking browsers that can run CCHome but users should update
	isDeprecatedBrowser() {
		return (
			!this.isUnsupportedBrowser() && bowser.isUnsupportedBrowser({ safari: '10' }, window.navigator.userAgent)
		);
	}

	isUnsupportedIE11() {
		return (
			window.location.pathname.indexOf('/quick-actions') === 0 &&
			bowser.isUnsupportedBrowser({ msie: '12' }, window.navigator.userAgent)
		);
	}

	/**
	 * compareVersionNumbers compares two strings of dot separated integers such that we
	 * return 1 if a > b
	 * return -1 if a < b
	 * return 0 if a === b
	 * e.g. compareVersionNumbers(1.0, 1.0.1) returns -1,
	 * compareVersionNumbers(2.0, 1.0.3) returns 1,
	 * compareVersionNumbers(3.0, 3.0.0) returns 0
	 */
	compareVersionNumbers(a, b) {
		const aParts = a.split('.').map((str) => parseInt(str, 10));
		const bParts = b.split('.').map((str) => parseInt(str, 10));
		const maxParts = aParts.length > bParts.length ? aParts : bParts;
		for (let i = 0; i < maxParts.length; i++) {
			if (aParts[i] === undefined) {
				aParts[i] = 0;
			} else if (bParts[i] === undefined) {
				bParts[i] = 0;
			}
			if (aParts[i] > bParts[i]) {
				return 1;
			} else if (aParts[i] < bParts[i]) {
				return -1;
			}
		}
		return 0;
	}

	getUserId() {
		if (window.adobeIMS) {
			const userProfile = window.adobeIMS.getUserProfile();
			return (userProfile && (userProfile.authId || userProfile.userId)) || '';
		}
		return '';
	}

	getUserCountryCode() {
		if (window.adobeIMS) {
			const userProfile = window.adobeIMS.getUserProfile();
			return (userProfile && userProfile.countryCode) || '';
		}
		return '';
	}

	getUserEmail() {
		if (window.adobeIMS) {
			const userProfile = window.adobeIMS.getUserProfile();
			return (userProfile && userProfile.email) || '';
		}
		return '';
	}

	getUserDisplayName() {
		if (window.adobeIMS) {
			const userProfile = window.adobeIMS.getUserProfile();
			return (userProfile && userProfile.displayName) || '';
		}
		return '';
	}

	getLocale() {
		if (window.adobeIMS) {
			const locale = window.adobeIMS.getLocale();
			return locale || '';
		}
		return '';
	}

	getAccessToken() {
		if (window.adobeIMS) {
			const accessTokenInfo = window.adobeIMS.getAccessTokenInfo();
			return (accessTokenInfo && accessTokenInfo.access_token) || '';
		}
		return '';
	}

	isSignedUser() {
		if (window.adobeIMS && window.adobeIMS.isSignedInUser && window.adobeIMS.isSignedInUser(false)) {
			return true;
		}
		return false;
	}

	isAdmin() {
		const possibleAdminRoles = [
			'deployment_admin',
			'org_admin',
			'storage_admin',
			'product_admin',
			'license_admin',
			'user_group_admin',
			'support_admin',
			'license_dev_admin',
		];
		let isAdmin = false;

		if (window.adobeIMS) {
			const userProfile = window.adobeIMS.getUserProfile();
			const roles = userProfile && userProfile.roles;
			if (roles && roles.length > 0) {
				roles.forEach((role) => {
					if (role.named_role && possibleAdminRoles.indexOf(role.named_role.toLowerCase()) !== -1) {
						isAdmin = true;
					}
				});
			}
		}
		return isAdmin;
	}

	/* supports 2x resolution or better */
	supportsRetinaDisplay() {
		return window.devicePixelRatio > 1;
	}

	/**
	 * returns true if device is touch based
	 *
	 * @returns true or false
	 */
	isTouchDevice() {
		return DeviceDetectionUtil.isTouchDevice();
	}

	isLinux() {
		return DeviceDetectionUtil.isLinux();
	}

	isMobileOrTab() {
		return DeviceDetectionUtil.isMobileOrTab();
	}

	/**
	 * Detects if the current browser is a mobile or tablet - check the useragent to match for ios, android and windows phone
	 */
	isMobileOrTabletBrowser() {
		let check = false;
		const input = navigator.userAgent || navigator.vendor || window.opera;
		// https://www.neowin.net/news/ie11-fakes-user-agent-to-fool-gmail-in-windows-phone-81-gdr1-update
		// We need to add the check for window.MSStream because IE-11 fakes it's user-agent.
		if (
			/windows phone/i.test(input) ||
			/android/i.test(input) ||
			(/iPad|iPhone|iPod/.test(input) && !window.MSStream)
		) {
			check = true;
		}
		return check;
	}

	isIPhone() {
		return DeviceDetectionUtil.isIPhone();
	}

	isIPad() {
		return DeviceDetectionUtil.isIPad();
	}

	isAndroid() {
		let check = false;
		const input = navigator.userAgent || navigator.vendor || window.opera;
		// https://www.neowin.net/news/ie11-fakes-user-agent-to-fool-gmail-in-windows-phone-81-gdr1-update
		// We need to add the check for window.MSStream because IE-11 fakes it's user-agent.
		if (/android/i.test(input) && !window.MSStream) {
			check = true;
		}
		return check;
	}

	isIOS() {
		let check = false;
		const input = navigator.userAgent || navigator.vendor || window.opera;
		// https://www.neowin.net/news/ie11-fakes-user-agent-to-fool-gmail-in-windows-phone-81-gdr1-update
		// We need to add the check for window.MSStream because IE-11 fakes it's user-agent.
		if (/iPad|iPhone|iPod/.test(input) && !window.MSStream) {
			check = true;
		}
		return check;
	}

	// get position top and left of an element inside a window
	offset(el) {
		const rect = el.getBoundingClientRect();
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
	}

	onResize() {
		const body = document.querySelector('body');
		if (body) {
			const newBreakpoint = window
				.getComputedStyle(body, '::after')
				.getPropertyValue('content')
				.replace(/"/g, '');

			/* istanbul ignore if */
			if (_currentBreakpoint !== newBreakpoint) {
				_currentBreakpoint = newBreakpoint;
				const event = new CustomEvent('breakpointChanged', { detail: { name: newBreakpoint } });
				window.dispatchEvent(event);
			}
		}
	}

	get currentBreakpoint() {
		this.onResize();
		return _currentBreakpoint;
	}

	/* istanbul ignore next */
	forceCarouselResize(index) {
		const event = new CustomEvent('forceCarouselResize', { detail: { forceCarouselResize: true, index } });
		window.dispatchEvent(event);
	}

	setDocumentTitle(title) {
		document.title = title;

		const titleElement = document.querySelector('title');

		/* istanbul ignore else */
		if (titleElement) {
			titleElement.innerHTML = title;
		}
	}

	/**
	 * This method checks to see if the supplied type of storage is available for use. Taken from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
	 * @param {string} type the type of storage to check for.
	 */
	storageAvailable(type) {
		let storage;
		try {
			if (type === 'cookie') {
				let cookieEnabled = navigator.cookieEnabled;
				if (!cookieEnabled) {
					document.cookie = 'testcookie';
					cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
				}
				return cookieEnabled;
			}
			storage = window[type];
			const x = '__storage_test__';
			storage.setItem(x, x);
			storage.removeItem(x);
			return true;
		} catch (e) {
			/* istanbul ignore next */
			typeof storage === 'undefined' &&
				window.cchome.util.logger.warn('Globals', 'Storage Unavailable', e.toString());

			/* istanbul ignore next */
			return (
				e instanceof DOMException && // everything except Firefox
				(e.code === 22 || // Firefox
					e.code === 1014 || // test name field too, because code might not be present
					// everything except Firefox
					e.name === 'QuotaExceededError' || // Firefox
					e.name === 'NS_ERROR_DOM_QUOTA_REACHED') && // acknowledge QuotaExceededError only if there's something already stored
				typeof storage !== 'undefined' &&
				storage.length !== 0
			);
		}
	}

	/**
	 * Prevent Body to Scroll
	 */
	disableBodyScroll() {
		// Only accept touchmove from fixed-element

		/* istanbul ignore else */
		if (document.body) {
			// Bug https://jira.corp.adobe.com/browse/CCH-3515
			// touchmove events seems to not removable on Safari iOS 10 and below

			/* istanbul ignore else */
			if (!this.isIOS && !bowser.isUnsupportedBrowser({ safari: '10' }, window.navigator.userAgent)) {
				/* istanbul ignore next */
				document.body.addEventListener(
					'touchmove',
					/* istanbul ignore else */
					(e) => e.preventDefault(),
				);
			}
			// $FlowFixMe
			document.body.classList.add('prevent-scrolling');
		}
	}

	/**
	 * Add scrolling back to Body
	 */
	enableBodyScroll() {
		/* istanbul ignore else */
		if (document.body) {
			document.body.removeEventListener(
				'touchmove',
				/* istanbul ignore else */
				(e) => e.preventDefault(),
			);
			// $FlowFixMe
			document.body.classList.remove('prevent-scrolling');
		}
	}

	/**
	 * Prevent HTML to Scroll
	 */
	disableHTMLScroll() {
		// Only accept touchmove from fixed-element

		/* istanbul ignore else */
		if (document.documentElement) {
			// $FlowFixMe
			document.documentElement.classList.add('prevent-scrolling');
		}
	}

	/**
	 * Add scrolling back to Body
	 */
	enableHTMLScroll() {
		/* istanbul ignore else */
		if (document.documentElement) {
			// $FlowFixMe
			document.documentElement.classList.remove('prevent-scrolling');
		}
	}

	/**
	 * Use the Murmur3 algorithm to hash user id into range from 0 to 99
	 * @param {string} test the name of the specific test this hash will be used for. See ExperimentationUtils.js for details.
	 * If a test is not specified, this function will return the hash using the default seed value.
	 * @return number
	 */
	murmurHashUserId(test: string) {
		return murmurHashUserId(this.getUserId(), test);
	}

	/**
	 * Determine if current user is in test for 20181129_Onboarding without accounting for other constrains
	 * @return boolean
	 */

	/* istanbul ignore next */
	isInTestBucket20181129Onboarding() {
		const result = window.cchome.util.murmurHashUserId('FIRST_TEST_RUN') < 50;
		return result;
	}

	/**
	 * Determine bucket of current user for 20181214_Search
	 * @return '', 'control', 'testA' or 'testB'
	 */

	/* istanbul ignore next */
	testBucket20181214Search() {
		let testBucket = '';
		const browserIsChrome = this.isChrome();
		const isDesktop = !this.isMobileOrTabletBrowser();
		const isEnglish = this.getStartupLanguage() === 'en';
		if (browserIsChrome && isDesktop && isEnglish) {
			const murmurHashUserId = this.murmurHashUserId('SECOND_TEST_RUN');
			if (murmurHashUserId >= 75 || this.hasFeature('temp_second_run_test_B')) {
				testBucket = 'testB';
			} else if (murmurHashUserId >= 50 || this.hasFeature('temp_second_run_test_A')) {
				testBucket = 'testA';
			} else {
				testBucket = 'control';
			}
		}
		return testBucket;
	}

	/**
	 * Returns the test bucket for the 2019-04-23 New Search Results Page test.
	 * @returns {string} string indicating the bucket for the New SRP test
	 */
	testBucket20190430CCSearchRollout() {
		let testBucket = '';
		// The getBucket method will return the bucket if the flag is enabled for the user.
		// If the flag is turned off for the environment or the user falls into control,
		// the getBucket method returns -1
		const bucket = parseInt(window.cchome.util.getBucket('20190430-CCSearch-Rollout'));
		switch (bucket) {
			case 2:
				testBucket = 'testB';
				break;
			case 1:
				testBucket = 'testA';
				break;
			default:
				testBucket = 'control';
				break;
		}

		AnalyticsUtil.experiment['20190430-CCSearch-Rollout'] = testBucket;

		return testBucket;
	}

	/**
	 * Returns the test bucket search bar/icon test.
	 * @returns {string} string indicating the bucket for the New SRP test
	 */
	testBucket20190715SearchBarIconTest() {
		let testBucket = '';
		// const isDesktop = !this.isMobileOrTabletBrowser();
		// const isEnglish = this.getStartupLanguage() === 'en';

		// The getBucket method will return the bucket if the flag is enabled for the user.
		// If the flag is turned off for the environment or the user falls into control,
		// the getBucket method returns -1
		const bucket = parseInt(window.cchome.util.getBucket('20190715-search-bar-icon-test'));
		switch (bucket) {
			case 2:
				testBucket = 'testSearchBarB';
				break;
			case 1:
				testBucket = 'testSearchBarA';
				break;
			default:
				testBucket = 'control';
				break;
		}

		AnalyticsUtil.experiment['20190715-search-bar-icon-test'] = testBucket;

		return testBucket;
	}

	/**
	 * Returns experiment segment for ui intervention test.
	 * @returns {string} string indicating the bucket for the New SRP test
	 */
	testBucket20190606UIinterventionTest() {
		let testExperience = '';
		const isDesktop = !this.isMobileOrTabletBrowser();
		const isEnglish = this.getStartupLanguage() === 'en';
		if (isDesktop && isEnglish) {
			testExperience = this.hasFeature('20190610_intervention_UI') ? 'testA-AC' : 'control';
			AnalyticsUtil.experiment['CCSearch-Results-AppCards-20190605'] = testExperience;
		}

		return testExperience;
	}

	/**
	 * Returns experiment segment for passing USS Contextual paramaters test.
	 * @returns {string} string indicating the bucket for the USS Contextual paramaters test
	 */
	testBucket20190815USSContextualParametersTest() {
		let testExperience = '';
		testExperience = this.hasFeature('uss_ctx_ranker_helpx_ff') ? 'testA' : 'control';
		AnalyticsUtil.experiment['uss_ctx_ranker_helpx_ff'] = testExperience; // eslint-disable-line
		return testExperience;
	}

	testBucket20190916SearchQuickLinksTest() {
		let testExperience = '';
		testExperience = this.hasFeature('20191111-search-quick-links') ? 'test' : 'control';
		AnalyticsUtil.experiment['20191111-search-quick-links'] = testExperience; // eslint-disable-line
		return testExperience;
	}

	/**
	 * Check locale param > tempLocale param (for tutorials) > cc cookie > Acom cookie > browser language
	 */
	getStartupLanguage() {
		const urlParams: any = url.parse(window.location.href, true).query;
		// console.log('urlParams', urlParams, window.location.href);

		let lang;
		// if locale param is in URL, update cookie
		const langUrlParam = window.location.pathname.split('/')[1];
		if (supportedLanguages.indexOf(langUrlParam) > -1) {
			lang = langUrlParam;
			setCCHomeLangCookie(lang);
		}

		if (!lang) {
			if (urlParams.locale && getAppLocale('cchome', urlParams.locale)) {
				lang = getAppLocale('cchome', urlParams.locale);
				setCCHomeLangCookie(lang);
			}
			// if tempLocale is provided use that language, on language switching the user will be able to change language
			else if (urlParams.tempLocale && getAppLocale('cchome', urlParams.tempLocale)) {
				lang = getAppLocale('cchome', urlParams.tempLocale);
			} else if (getCookie('creative-cloud-loc') && getAppLocale('cchome', getCookie('creative-cloud-loc'))) {
				lang = getAppLocale('cchome', getCookie('creative-cloud-loc'));
			} else if (getCookie('international') && getAppLocale('cchome', getCookie('international'))) {
				lang = getAppLocale('cchome', getCookie('international'));
			} else if (
				navigator.languages &&
				navigator.languages[0] &&
				getAppLocale('cchome', navigator.languages[0])
			) {
				lang = getAppLocale('cchome', navigator.languages[0]);
			} else if (navigator.language && getAppLocale('cchome', navigator.language)) {
				/* istanbul ignore next */
				lang = getAppLocale('cchome', navigator.language);
			} else if (navigator.browserLanguage && getAppLocale('cchome', navigator.browserLanguage)) {
				/* istanbul ignore next */
				lang = getAppLocale('cchome', navigator.browserLanguage);
			} else {
				/* istanbul ignore next */
				lang = 'en';
			}
		}
		return lang;
	}

	/**
	 * Sets a boolean to indicate if this is the user's first visit to CC Home.
	 * This is currently set by the TransitionScreen component when it displays.
	 * @param {bool} isFirstVisit
	 */
	setIsFirstVisit(firstVisit: boolean) {
		this.firstVisit = firstVisit;
	}

	/**
	 * Returns a boolean to indicate whether this is the user's first visit to CC Home.
	 */
	isFirstVisit() {
		return this.firstVisit;
	}

	/**
	 * Clear out event_referrer at start up to get rid of cruft
	 */
	removeReferrer() {
		/* istanbul ignore else */
		if (this.storageAvailable('sessionStorage')) {
			sessionStorage.removeItem('event_referrer');
		}
	}

	/**
	 * Set event_referrer in local stoage for analytics and for search. This information is used
	 * in the Ingest module and for Search Results Page
	 * @param {*} prevLocation
	 */
	setReferrer(prevLocation: string) {
		let prevLoc = prevLocation;
		if (prevLoc === '/') {
			prevLoc = '/home';
		}

		const eventReferrer = `${window.location.origin}${prevLoc}`;
		const eventUrl = window.location.pathname === '/' ? `${window.location.href}home` : window.location.href;

		if (this.storageAvailable('sessionStorage')) {
			sessionStorage.setItem('event_referrer', JSON.stringify({ referrer: eventReferrer, url: eventUrl }));
		}
	}

	/**
	 * Set ingest_url_params in session stoage for analytics. This information is used
	 * in the Ingest module.
	 */
	setQueryParams() {
		if (this.storageAvailable('sessionStorage') && !sessionStorage.getItem('ingest_url_params')) {
			const urlParams: any = url.parse(window.location.href, true).query;
			sessionStorage.setItem('ingest_url_params', JSON.stringify(urlParams));
		}
	}

	/**
	 * Get an array of PerformanceResourceTimings for each specific request
	 * https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming
	 *
	 * @param {string} url
	 * @return Array<PerformanceResourceTimings>
	 */
	getPerformanceFromRequest(url: string) {
		if (
			window.performance &&
			window.performance.getEntriesByType &&
			typeof window.performance.getEntriesByType === 'function'
		) {
			const allResources = window.performance.getEntriesByType('resource');
			if (allResources) {
				const performanceForRequest = allResources.filter((e) => e.name === url);
				return performanceForRequest;
			}
		}
		return [];
	}

	/**
	 * returns true if device is ARM based
	 *
	 * @returns true or false
	 */
	isARM() {
		return DeviceDetectionUtil.isARM();
	}

	/**
	 * returns true if device is Chrome OS based. Eg: a chromebook
	 *
	 * @returns true or false
	 */
	isChromeOS() {
		return DeviceDetectionUtil.isChromeOS();
	}
}

// Polyfill CustomEvent for IE11

/* istanbul ignore next */
(() => {
	// eslint-disable-line wrap-iife
	if (typeof window.CustomEvent === 'function') {
		return false;
	}

	function CustomEvent(event, params) {
		const newParams = params || { bubbles: false, cancelable: false, detail: undefined };
		const evt = document.createEvent('CustomEvent');
		evt.initCustomEvent(event, newParams.bubbles, newParams.cancelable, newParams.detail);
		return evt;
	}

	CustomEvent.prototype = window.Event.prototype;
	// @ts-ignore
	window.CustomEvent = CustomEvent;
})();

// polyfill for performance api
(function () {
	if ('performance' in window === false) {
		// @ts-ignore
		window.performance = {};
	}

	Date.now =
		Date.now ||
		function () {
			return new Date().getTime();
		};

	if ('now' in window.performance === false) {
		let nowOffset = Date.now();

		if (performance.timing && performance.timing.navigationStart) {
			nowOffset = performance.timing.navigationStart;
		}

		window.performance.now = function now() {
			return Date.now() - nowOffset;
		};
	}
})();

// For IE 10 and earlier

/* istanbul ignore if */
if (!window.location.origin) {
	// @ts-ignore
	window.location.origin =
		window.location.protocol +
		'//' +
		window.location.hostname +
		(window.location.port ? ':' + window.location.port : ''); // eslint-disable-line prefer-template
}

window.cchome = window.cchome || {};
window.cchome.util = new Utils();
window.cchome.util.logger = new LoggerUtils();
window.cchome.util.localStorage = new LocalStorageUtils();
window.cchome.util.sessionStorage = new SessionStorageUtils();
window.cchome.util.geo = {};
window.cchome.util.addFeaturesArray = [];
window.cchome.util.removeFeaturesArray = [];
window.cchome.util.optOutAnalytics = false;
window.cchome.util.optOutMachineLearning = false;
window.cchome.util.optOutAnalyticsInited = false;
